import { Autocomplete, Box, IconButton, TableCell, TableRow, TextField, Typography, useMediaQuery } from "@mui/material";
import { useState, useMemo } from "react";
import { Check, Close } from "@mui/icons-material";
import { calcHours, toISO } from "../helpers/calculate-hours.helpers";
import { TimesheetRow } from "../../../interfaces/time-sheet.interfaces";
import { colors } from "../../../../ui/colors";

interface Props {
  formatedTimeSheets: Partial<TimesheetRow>[];
  onSave: (data: { jobEventUid: string; workforceUid: string; startAt: string; endAt: string }) => void;
  onCancel: () => void;
}

export default function AddTimeRow({ onSave, onCancel, formatedTimeSheets }: Props) {
  const [selectedJob, setSelectedJob] = useState<(typeof formatedTimeSheets)[0] | null>(null);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)");
  const totalHours = useMemo(() => calcHours(startAt, endAt), [startAt, endAt]);
  const canSave = !!selectedJob && !!startAt && !!endAt && totalHours !== null;

  return (
    <TableRow
      sx={{
        bgcolor: "var(--fx-colors-warning-50, #FFF7ED)",
        "& td": {
          borderBottom: "2px solid #fdba74",
          py: 1.5,
          px: 1,
        },
      }}
    >
      <TableCell colSpan={2}>
        <Autocomplete
          value={selectedJob}
          options={formatedTimeSheets}
          getOptionLabel={(o) => o.jobAlias || ""}
          onChange={(_, val) => setSelectedJob(val)}
          size="small"
          renderInput={(params) => <TextField {...params} label="Service" placeholder="Select job..." sx={fieldSx} />}
        />
      </TableCell>

      <TableCell colSpan={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Start"
            type="time"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            sx={fieldSx}
          />
          <TextField
            label="End"
            type="time"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            sx={fieldSx}
          />
        </Box>
      </TableCell>

      <TableCell colSpan={2} align="center">
        <Box sx={{ minWidth: "80px" }}>
          {totalHours !== null ? (
            <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#16a34a" }}>{totalHours}</Typography>
          ) : startAt && endAt ? (
            <Typography sx={{ fontSize: 10, color: "#ef4444", fontWeight: 700 }}>INVALID</Typography>
          ) : (
            <Typography sx={{ fontSize: 10, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>-- : --</Typography>
          )}
        </Box>
      </TableCell>

      <TableCell align="right">
        <Box display="flex" justifyContent="flex-end" gap={1}>
          {!canSave ? (
            <Typography sx={{ color: colors.orange.base, fontWeight: 700, fontSize: 10, alignSelf: "center" }}>MISSING INFO</Typography>
          ) : (
            <IconButton
              size="small"
              onClick={() =>
                onSave({
                  jobEventUid: selectedJob!.uid,
                  workforceUid: selectedJob!.workforceUid,
                  startAt: toISO(startAt, selectedJob?.startDate || ""),
                  endAt: toISO(endAt, selectedJob?.endDate || ""),
                })
              }
              sx={{ bgcolor: "var(--fx-colors-success-100, #D1FAE5)", color: "#16a34a" }}
            >
              <Check sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton size="small" onClick={onCancel} sx={{ bgcolor: "var(--fx-colors-danger-100, #FEE2E2)", color: "#ef4444" }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}
const fieldSx = {
  width: "100%",
  "& .MuiInputLabel-root": { fontSize: 12 },
  "& .MuiInputLabel-root.Mui-focused": { color: colors.orange.base },
  "& .MuiOutlinedInput-root": {
    fontSize: 13,
    borderRadius: "8px",
    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
    "&.Mui-focused fieldset": { borderColor: colors.orange.base },
  },
};
